/**
 * MFA (Multi-Factor Authentication) constants.
 * Values copied verbatim from ss-ui-client/src/global/step-up-authentication/constants.js,
 * shipstation-lite/src/features/mfa/mfa-constants.ts, and ss-ui-client/src/authentication/constants.js
 */

// ---- Auth0 Challenge Types ----
export const CHALLENGE_TYPE_OOB = 'oob' as const;
export const CHALLENGE_TYPE_OTP = 'otp' as const;
export const CHALLENGE_TYPE_RECOVERY_CODE = 'recovery_code' as const;

export const CHALLENGE_TYPES = {
  OOB: CHALLENGE_TYPE_OOB,
  OTP: CHALLENGE_TYPE_OTP,
  RECOVERY_CODE: CHALLENGE_TYPE_RECOVERY_CODE,
} as const;
export type ChallengeType = (typeof CHALLENGE_TYPES)[keyof typeof CHALLENGE_TYPES];

// ---- Auth0 Management Types ----
export const MANAGEMENT_TYPES = {
  EMAIL: 'email',
  PHONE: 'phone',
  RECOVERY_CODE: 'recovery-code',
  TOTP: 'totp',
  PASSKEY: 'passkey',
  PUSH: 'push',
} as const;
export type ManagementType = (typeof MANAGEMENT_TYPES)[keyof typeof MANAGEMENT_TYPES];

// ---- Type Mapping ----
export const mapManagementApiTypeToAuthenticationApiChallengeType = (managementType: ManagementType): ChallengeType => {
  const mapping: Record<ManagementType, ChallengeType> = {
    [MANAGEMENT_TYPES.EMAIL]: CHALLENGE_TYPES.OOB,
    [MANAGEMENT_TYPES.PHONE]: CHALLENGE_TYPES.OOB,
    [MANAGEMENT_TYPES.RECOVERY_CODE]: CHALLENGE_TYPES.RECOVERY_CODE,
    [MANAGEMENT_TYPES.TOTP]: CHALLENGE_TYPES.OTP,
    [MANAGEMENT_TYPES.PASSKEY]: CHALLENGE_TYPES.OTP,
    [MANAGEMENT_TYPES.PUSH]: CHALLENGE_TYPES.OOB,
  };
  return mapping[managementType] ?? CHALLENGE_TYPES.OTP;
};

// ---- Token Timeout Error ----
export const TOKEN_TIMEOUT_ERROR = {
  code: 6000,
  message: 'The mfa_token provided is invalid. Try getting a new token.',
  message2: 'mfa_token is expired',
} as const;

// ---- Step-Up Auth Phases ----
export const PHASES = {
  TRYING_WITHOUT_PASSWORD: 'TRYING_WITHOUT_PASSWORD',
  PASSWORD: 'PASSWORD',
  CHOOSE: 'CHOOSE',
  CHALLENGE: 'CHALLENGE',
  VERIFIED: 'VERIFIED',
  ERROR: 'ERROR',
} as const;
export type Phase = (typeof PHASES)[keyof typeof PHASES];

// ---- Additional Verification Flows ----
export const ADDITIONAL_VERIFICATION_FLOWS = {
  mfaRequired: 'mfa_required',
  emailVerificationRequired: 'email_verification_required',
} as const;

// ---- MFA Cancelled Error ----
export const MFA_CANCELLED_ERROR = 'MFA authentication cancelled by user' as const;

// ---- Phone Verification States ----
export const PHONE_VERIFICATION_STATES = {
  START: 'start',
  BANNER: 'banner',
  MODAL: 'modal',
  UNSKIPPABLE: 'unskippable',
  VERIFIED: 'verified',
  BYPASSED_VIA_OTP: 'bypassed_via_otp',
  BANNER_DISMISSED: 'banner_dismissed',
  MODAL_DISMISSED: 'modal_dismissed',
} as const;
export type PhoneVerificationState = (typeof PHONE_VERIFICATION_STATES)[keyof typeof PHONE_VERIFICATION_STATES];

// ---- Auth0 MFA Types ----
export const AUTH0_MFA_TYPES = {
  email: 'email',
  sms: 'sms',
  voice: 'voice',
  totp: 'totp',
} as const;

// ---- Analytics Events ----
export const MFA_ANALYTICS_EVENTS = {
  codeChallenge: 'MFA Step Up Code Challenge',
  passwordChallenge: 'MFA Step Up Password Challenge',
  success: 'MFA Step Up Success',
} as const;

// ---- API Endpoint Paths ----
export const MFA_ENDPOINT_START_STEP_UP = '../mfa/v2/start-step-up-auth' as const;
export const MFA_ENDPOINT_START_CHALLENGE = '../mfa/start-step-up-auth-mfa-challenge' as const;
export const MFA_ENDPOINT_SUBMIT_CHALLENGE = '../mfa/v2/submit-step-up-auth-mfa-challenge' as const;
export const MFA_ENDPOINT_GET_ENROLLMENTS = '../mfa/get-enrollments' as const;
export const MFA_ENDPOINT_START_NEW_ENROLLMENT = '../mfa/start-new-enrollment-verification' as const;
export const MFA_ENDPOINT_REMOVE_ENROLLMENT = '../mfa/remove-enrollment' as const;

// ---- Auth0 Out-Of-Band Channels ----
export const OOB_CHANNEL_EMAIL = 'email' as const;
export const OOB_CHANNEL_SMS = 'sms' as const;
export const OOB_CHANNEL_VOICE = 'voice' as const;

export const OOB_CHANNELS = [OOB_CHANNEL_EMAIL, OOB_CHANNEL_SMS, OOB_CHANNEL_VOICE] as const;
export type OobChannel = (typeof OOB_CHANNELS)[number];

// ---- Enrollment Methods ----
export const MFA_ENROLLMENT_METHODS = [AUTH0_MFA_TYPES.totp, AUTH0_MFA_TYPES.sms, AUTH0_MFA_TYPES.voice, AUTH0_MFA_TYPES.email] as const;
export type MfaEnrollmentMethod = (typeof MFA_ENROLLMENT_METHODS)[number];

/**
 * ss-ui-client opens the MFA modals from inside another modal, so they default
 * above Mantine's own stacking range.
 */
export const MFA_MODAL_DEFAULT_Z_INDEX = 10000 as const;

// ---- Enrollment Phases ----
export const ENROLLMENT_PHASE_SELECT_METHOD = 'SELECT_METHOD' as const;
export const ENROLLMENT_PHASE_DETAILS = 'DETAILS' as const;
export const ENROLLMENT_PHASE_CHALLENGE = 'CHALLENGE' as const;

export const ENROLLMENT_PHASES = [ENROLLMENT_PHASE_SELECT_METHOD, ENROLLMENT_PHASE_DETAILS, ENROLLMENT_PHASE_CHALLENGE] as const;
export type EnrollmentPhase = (typeof ENROLLMENT_PHASES)[number];

/**
 * Stands in for the Auth0 authenticator id while an enrollment is still pending.
 * Auth0 only issues the real id once the enrollment is confirmed, but
 * `ChallengePhase` keys its copy off `enrollment.id`, so the pending enrollment
 * carries `{method}|{TEMP_ENROLLMENT_ID_SUFFIX}`.
 */
export const TEMP_ENROLLMENT_ID_SUFFIX = 'temp-id' as const;
